import React, { type FC, type PropsWithChildren, useEffect } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'

import { ConnectwareError, selectInfo, StatusType, type SystemConnectivity, Translation } from '../../../../domain'

import { CircularLoader, Status, Table } from '../../common'
import { ErrorMessage } from '../../ErrorMessage'
import { useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'

const wrapperStyle: SxProps<Theme> = { py: 4 }

const Wrapper: FC<PropsWithChildren> = ({ children }) => {
    return (
        <Box data-testid="info-preflight" sx={wrapperStyle}>
            {children}
        </Box>
    )
}

type Endpoints = SystemConnectivity['endpoints']

type ConnectivityLine = Readonly<{ name: string, info: string, status: StatusType }>

/** Plain text, so the table's search and sort work on what is shown */
const useConnectivityLines = (endpoints: Endpoints): ReadonlyArray<ConnectivityLine> => {
    const translator = useTranslator()

    const toLine = (type: keyof Endpoints, name: Translation, detail: string): ConnectivityLine => {
        const { url, reachable } = endpoints[type]
        const reachability = translator.formatTranslation(Translation.IS_URL_REACHABLE, { url: url.toString(), reachable })
        return {
            name: translator.formatTranslation(name),
            info: `${reachability} ${detail}`,
            status: reachable ? StatusType.REACHABLE : StatusType.UNREACHABLE,
        }
    }

    return [
        toLine(
            'cybusPortal',
            Translation.CYBUS_PORTAL,
            translator.formatTranslation(Translation.CYBUS_PORTAL_STATUS, { updatesAutomatically: endpoints.cybusPortal.reachable })
        ),
        toLine(
            'cybusRegistry',
            Translation.CYBUS_REGISTRY,
            translator.formatTranslation(Translation.CYBUS_REGISTRY_STATUS, { installable: endpoints.cybusRegistry.reachable })
        ),
        toLine(
            'dockerHub',
            Translation.DOCKER_HUB,
            translator.formatTranslation(Translation.DOCKER_HUB_STATUS, { installable: endpoints.dockerHub.reachable })
        ),
    ]
}

const ConnectivityTable: FC<Readonly<{ endpoints: Endpoints }>> = ({ endpoints }) => {
    const lines = useConnectivityLines(endpoints)

    return (
        <Table<ConnectivityLine>
            data={lines}
            columns={{
                name: { label: Translation.NAME, sort: true },
                info: { label: Translation.INFO },
                status: { label: Translation.STATUS, customCellRender: (status) => <Status status={status} /> },
            }}
        />
    )
}

export const InternetConnectivity: FC = () => {
    const infoUsecase = useAppUsecase('informationUsecase')
    useEffect(() => infoUsecase.loadInformation(), [])

    const info = useAppState(selectInfo)

    if (info === null) {
        return null
    }

    if (ConnectwareError.is(info.connectivity)) {
        return (
            <Wrapper>
                <ErrorMessage error={info.connectivity} extras="popover" />
            </Wrapper>
        )
    }

    if (info.connectivity === null) {
        return (
            <Wrapper>
                <CircularLoader />
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <ConnectivityTable endpoints={info.connectivity.endpoints} />
        </Wrapper>
    )
}
